import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Button,
  Divider,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  FitnessCenter as FitnessCenterIcon,
  PlayArrow as PlayArrowIcon,
  Image as ImageIcon,
  Build as EquipmentIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import {
  getWorkoutDayColor,
  getRestDayColor,
} from "../theme/TrainingPlanTheme";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  buildPrescriptionSummary,
  getPrescriptionLabel,
} from "../utils/prescriptionFormatting";

/**
 * Detailed view for a specific workout day showing all exercises
 */
const WorkoutDayView = ({
  planData,
  dayIndex,
  onExerciseSelect,
  onBackToOverview,
  isRestDay,
  getExercise,
  formatExerciseName,
  brandingColors,
}) => {
  const split = planData.splits[dayIndex];
  const restDay = isRestDay(split);
  const dayColor = restDay
    ? getRestDayColor(brandingColors)
    : getWorkoutDayColor(dayIndex, brandingColors);

  const getTextColor = (backgroundColor) => {
    const hex = backgroundColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 140 ? "#000000" : "#ffffff";
  };

  const formatDayName = (index) => {
    return `Day ${index + 1}`;
  };

  const getExerciseType = (exercise) => {
    if (exercise.type === "super_set") return "Superset";
    return "Exercise";
  };

  const hasMedia = (exerciseId) => {
    const exercise = getExercise(exerciseId);
    return exercise.videos?.length > 0 || exercise.images?.length > 0;
  };

  const getEquipmentList = (exerciseId) => {
    const exercise = getExercise(exerciseId);
    return exercise.equipment || [];
  };

  if (!split) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Workout day not found.
        </Typography>
        <Button onClick={onBackToOverview} sx={{ mt: 2 }}>
          Back to Overview
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackToOverview}
          sx={{ mb: 2 }}
          color="primary"
        >
          Back to Overview
        </Button>

        {/* Day Header */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <Box
            sx={{
              backgroundColor: dayColor,
              color: getTextColor(dayColor),
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: getTextColor(dayColor),
                width: 48,
                height: 48,
              }}
            >
              <FitnessCenterIcon />
            </Avatar>

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {formatDayName(dayIndex)}
              </Typography>
              <Typography variant="h6" component="h2" sx={{ opacity: 0.9 }}>
                {split.name || (restDay ? "Rest Day" : "Workout")}
              </Typography>
            </Box>

            {!restDay && split.exercises && (
              <Chip
                label={`${split.exercises.length} exercises`}
                size="large"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: getTextColor(dayColor),
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* Description */}
      <Card elevation={1} sx={{ mb: 3, bgcolor: "grey.50" }}>
        <CardContent>
          <Box sx={{ "& p": { mb: 1 }, "& ul, & ol": { pl: 2, mb: 1 } }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {split.description ||
                (restDay
                  ? "Focus on recovery, rest, and regeneration. Take this day to let your body rest and rebuild."
                  : "Focus on proper form and controlled movements throughout each exercise.")}
            </ReactMarkdown>
          </Box>
        </CardContent>
      </Card>

      {/* Rest Day Content */}
      {restDay ? (
        <Card elevation={2}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3 }}>
              Rest Day Activities
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                "Light walking or gentle movement",
                "Stretching or mobility work",
                "Meditation or relaxation",
                "Proper hydration and nutrition",
                "Quality sleep (7-9 hours)",
              ].map((activity, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Chip
                    label={activity}
                    variant="outlined"
                    color="primary"
                    sx={{ width: "100%", justifyContent: "flex-start" }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Warm-up */}
          {split.warmup && (
            <Card
              elevation={1}
              sx={{
                mb: 3,
                bgcolor: "warning.light",
                color: "warning.contrastText",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Warm-up
                </Typography>
                <Typography variant="body1">{split.warmup}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Exercises */}
          {split.exercises && split.exercises.length > 0 && (
            <Box>
              <Typography
                variant="h5"
                component="h3"
                gutterBottom
                sx={{ mb: 3 }}
              >
                Exercises ({split.exercises.length})
              </Typography>

              <Grid container spacing={3}>
                {split.exercises.map((exercise, exerciseIndex) => {
                  const exerciseSummary = buildPrescriptionSummary(exercise);
                  const exercisePrescriptionLabel = getPrescriptionLabel(
                    exerciseSummary.type,
                  );

                  return (
                    <Grid item xs={12} key={exercise.id || exerciseIndex}>
                      <Card
                        elevation={2}
                        sx={{
                          transition:
                            "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        {exercise.type === "super_set" ? (
                          // Superset Layout
                          <Box>
                            {/* Superset Header */}
                            <Box
                              sx={{
                                backgroundColor: "primary.main",
                                color: "primary.contrastText",
                                p: 2,
                              }}
                            >
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                SUPERSET: {exercise.superSetName}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {exercisePrescriptionLabel}
                                {exerciseSummary.summaryChips.length > 0 && (
                                  <> • {exerciseSummary.summaryChips.join(" • ")}</>
                                )}
                              </Typography>
                              {exerciseSummary.detailRows.length > 0 && (
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  {exerciseSummary.detailRows
                                    .map(
                                      (row) => `${row.label}: ${row.value}`,
                                    )
                                    .join(" • ")}
                                </Typography>
                              )}
                            </Box>

                            <CardContent>
                              <Grid container spacing={2}>
                                {exercise.exercises.map(
                                  (subExercise, subIndex) => {
                                    const subSummary =
                                      buildPrescriptionSummary(subExercise);
                                    const subPrescriptionLabel =
                                      getPrescriptionLabel(subSummary.type);

                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        key={subExercise.id || subIndex}
                                      >
                                        <Card variant="outlined">
                                          <CardActionArea
                                            onClick={() =>
                                              onExerciseSelect(
                                                subExercise.exercise,
                                              )
                                            }
                                            sx={{ p: 2 }}
                                          >
                                            <Typography
                                              variant="h6"
                                              component="h4"
                                              gutterBottom
                                            >
                                              {formatExerciseName(
                                                subExercise.exercise,
                                              )}
                                            </Typography>

                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ fontWeight: 600, mb: 1 }}
                                            >
                                              {subPrescriptionLabel}
                                            </Typography>

                                            {subSummary.summaryChips.length >
                                              0 && (
                                              <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ mb: 1, flexWrap: "wrap" }}
                                              >
                                            {subSummary.summaryChips.map(
                                                  (chip, chipIndex) => (
                                                    <Chip
                                                      key={`${chip}-${chipIndex}`}
                                                      label={chip}
                                                      size="small"
                                                      color="primary"
                                                    />
                                                  ),
                                                )}
                                              </Stack>
                                            )}

                                            {subSummary.detailRows.length >
                                              0 && (
                                              <Stack
                                                spacing={0.5}
                                                sx={{ mb: 1 }}
                                              >
                                                {subSummary.detailRows.map(
                                                  (row) => (
                                                    <Typography
                                                      key={`${row.label}-${row.value}`}
                                                      variant="body2"
                                                      color="text.secondary"
                                                    >
                                                      {row.label}: {row.value}
                                                    </Typography>
                                                  ),
                                                )}
                                              </Stack>
                                            )}

                                            {/* Media indicators */}
                                            <Stack direction="row" spacing={1}>
                                              {hasMedia(
                                                subExercise.exercise,
                                              ) && (
                                                <Stack
                                                  direction="row"
                                                  spacing={0.5}
                                                  alignItems="center"
                                                >
                                                  <PlayArrowIcon
                                                    color="action"
                                                    fontSize="small"
                                                  />
                                                  <ImageIcon
                                                    color="action"
                                                    fontSize="small"
                                                  />
                                                </Stack>
                                              )}
                                              {getEquipmentList(
                                                subExercise.exercise,
                                              ).length > 0 && (
                                                <Stack
                                                  direction="row"
                                                  spacing={0.5}
                                                  alignItems="center"
                                                >
                                                  <EquipmentIcon
                                                    color="action"
                                                    fontSize="small"
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    Equipment needed
                                                  </Typography>
                                                </Stack>
                                              )}
                                            </Stack>
                                          </CardActionArea>
                                        </Card>
                                      </Grid>
                                    );
                                  },
                                )}
                              </Grid>
                            </CardContent>
                          </Box>
                        ) : (
                          // Regular Exercise Layout
                          <CardActionArea
                            onClick={() => onExerciseSelect(exercise.exercise)}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 2,
                                  mb: 2,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "primary.main",
                                    width: 40,
                                    height: 40,
                                  }}
                                >
                                  <FitnessCenterIcon />
                                </Avatar>

                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography
                                    variant="h6"
                                    component="h4"
                                    gutterBottom
                                  >
                                    {formatExerciseName(exercise.exercise)}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600, mb: 1 }}
                                  >
                                    {exercisePrescriptionLabel}
                                  </Typography>

                                  {exerciseSummary.summaryChips.length > 0 && (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      sx={{ mb: 1, flexWrap: "wrap" }}
                                    >
                                      {exerciseSummary.summaryChips.map(
                                        (chip, chipIndex) => (
                                          <Chip
                                            key={`${chip}-${chipIndex}`}
                                            label={chip}
                                            size="small"
                                            color="primary"
                                          />
                                        ),
                                      )}
                                    </Stack>
                                  )}

                                  {exerciseSummary.detailRows.length > 0 && (
                                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                                      {exerciseSummary.detailRows.map(
                                        (row) => (
                                          <Typography
                                            key={`${row.label}-${row.value}`}
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {row.label}: {row.value}
                                          </Typography>
                                        ),
                                      )}
                                    </Stack>
                                  )}

                                  {/* Notes */}
                                  {exercise.notes && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, mb: 1 }}
                                      >
                                        Notes:
                                      </Typography>
                                      <Box sx={{ "& p": { mb: 1 } }}>
                                        <ReactMarkdown
                                          remarkPlugins={[remarkGfm]}
                                        >
                                          {exercise.notes}
                                        </ReactMarkdown>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Media and Equipment indicators */}
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    {hasMedia(exercise.exercise) && (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                      >
                                        <PlayArrowIcon
                                          color="action"
                                          fontSize="small"
                                        />
                                        <ImageIcon
                                          color="action"
                                          fontSize="small"
                                        />
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Media available
                                        </Typography>
                                      </Stack>
                                    )}
                                    {getEquipmentList(exercise.exercise)
                                      .length > 0 && (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                      >
                                        <EquipmentIcon
                                          color="action"
                                          fontSize="small"
                                        />
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Equipment:{" "}
                                          {getEquipmentList(exercise.exercise)
                                            .slice(0, 2)
                                            .join(", ")}
                                          {getEquipmentList(exercise.exercise)
                                            .length > 2 && "..."}
                                        </Typography>
                                      </Stack>
                                    )}
                                  </Stack>
                                </Box>
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        )}
                      </Card>

                      {/* Alternative Exercises */}
                      {(exercise.alternatives || []).length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            pl: 2,
                            borderLeft: "3px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                            <SwapHorizIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Alternative exercises
                            </Typography>
                          </Stack>
                          <Stack spacing={1}>
                            {(exercise.alternatives || []).map((alt, altIdx) => {
                              const altSummary = buildPrescriptionSummary(alt);
                              const altLabel = getPrescriptionLabel(altSummary.type);
                              return (
                                <Card
                                  key={alt.id || altIdx}
                                  variant="outlined"
                                  sx={{ bgcolor: "grey.50" }}
                                >
                                  {alt.type === "super_set" ? (
                                    <Box>
                                      <Box sx={{ bgcolor: "action.hover", px: 2, py: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                          SUPERSET: {alt.superSetName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {altLabel}
                                          {altSummary.summaryChips.length > 0 && ` • ${altSummary.summaryChips.join(" • ")}`}
                                        </Typography>
                                      </Box>
                                      <CardContent sx={{ py: 1 }}>
                                        <Grid container spacing={1}>
                                          {(alt.exercises || []).map((child, childIdx) => {
                                            const childSummary = buildPrescriptionSummary(child);
                                            return (
                                              <Grid item xs={12} sm={6} key={child.id || childIdx}>
                                                <Box
                                                  onClick={() => onExerciseSelect(child.exercise)}
                                                  sx={{ cursor: "pointer", p: 1 }}
                                                >
                                                  <Typography variant="body2" fontWeight={600} color="primary">
                                                    {formatExerciseName(child.exercise)}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                    {getPrescriptionLabel(childSummary.type)}
                                                    {childSummary.summaryChips.length > 0 && ` • ${childSummary.summaryChips.join(" • ")}`}
                                                  </Typography>
                                                </Box>
                                              </Grid>
                                            );
                                          })}
                                        </Grid>
                                      </CardContent>
                                    </Box>
                                  ) : (
                                    <CardActionArea onClick={() => onExerciseSelect(alt.exercise)} sx={{ p: 1.5 }}>
                                      <Typography variant="body2" fontWeight={600} color="primary">
                                        {formatExerciseName(alt.exercise)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {altLabel}
                                        {altSummary.summaryChips.length > 0 && ` • ${altSummary.summaryChips.join(" • ")}`}
                                      </Typography>
                                      {alt.notes ? (
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                          {alt.notes}
                                        </Typography>
                                      ) : null}
                                    </CardActionArea>
                                  )}
                                </Card>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default WorkoutDayView;
